import React, { useMemo, useEffect } from "react";
import { useStore } from "../../store/store";
import fractionToInt from "../../util/fractionToInt";
import { alumConfigOptions, configOptions } from "../../initialValues";
import {
  taggedConfigImages,
  taggedPicoConfigImages,
} from "../../taggedImages/taggedConfigImages";
import taggedWindowImages from "../../taggedImages/taggedWindowImages";
import { specifyPrimary, noAccess, evenEven } from "./ConfigurationAccordian";
import singleDirectionOdds from "../../util/singleDirectionOdds";

interface UpdateObj {
  configuration?: string;
  exactConfig?: string;
  exactConfigImg?: string;
}

interface SelectedConfigUpdateObj extends UpdateObj {
  jamb: string;
  addScreen: string;
  screenPocket: string;
  screenColor: string;
  stilesAndRails: string;
  stileCategory: string;
  lockRail: string;
  primaryDoor?: string;
  handleStyle?: string;
  handleColor?: string;
}

type TaggedImage = {
  config: string;
  image1?: string;
  image2?: string;
  image1LH?: string;
  image1RH?: string;
  image2LH?: string;
  image2RH?: string;
};

const findBifoldImage = (
  option: string,
  doorType: string,
  alternateImages: boolean,
) => {
  const images: TaggedImage[] =
    doorType === "window"
      ? taggedWindowImages
      : alternateImages
        ? taggedPicoConfigImages
        : taggedConfigImages;

  var match: TaggedImage | undefined;

  if (images.length) {
    match = images.find((img) => img.config === option);
  }

  return match;
};

function getConfigImg(
  configImage: { [key: string]: string },
  primaryDoor: string,
  alternateImages: boolean,
  side: string,
) {
  const key = side === "left" ? "image1" : "image2";

  if (alternateImages) {
    return (primaryDoor === "Left" || !primaryDoor) && configImage[`${key}LH`]
      ? configImage[`${key}LH`]
      : primaryDoor === "Right" && configImage[`${key}RH`]
        ? configImage[`${key}RH`]
        : configImage[key]
          ? configImage[key]
          : "";
  }
  return configImage[key] || "";
}

function withSingleHandle(
  withHandle: string = "",
  handleStyle: string = "",
  config: string = "",
) {
  return (
    withHandle === "Add Handle Set" &&
    handleStyle === "Smart Lock *" &&
    specifyPrimary.includes(config)
  );
}

function useConfigSelection(
  bifoldType: string,
  hideHandlesWithEvenConfigs: (hide: boolean) => void,
) {
  const addToQuote = useStore((state) => state.addToQuote);

  const unitSize = useStore((state) => state.quote.unitSize);
  const unitSizeFraction = useStore((state) => state.quote.unitSizeFraction);

  let netWidth = fractionToInt(unitSize, unitSizeFraction);

  const configuration = useStore((state) => state.quote.configuration);
  const exactConfig = useStore((state) => state.quote.exactConfig);
  const primaryDoor = useStore((state) => state.quote.primaryDoor);

  const withExtSbolt = useStore((state) => state.quote.withExtSbolt);

  const stileCategory = useStore((state) => state.quote.stileCategory);

  const hingeColor = useStore((state) => state.quote.hingeColor);
  const shootBoltColor = useStore((state) => state.quote.shootBoltColor);

  const material = useStore((state) => state.quote.material);

  const isAlum = material === "All Aluminum";

  const withHandle = useStore((state) => state.quote.withHandle);
  const handleStyle = useStore((state) => state.quote.handleStyle);

  const configurationOptions = useMemo(() => {
    return isAlum
      ? alumConfigOptions(netWidth, bifoldType === "window")
      : configOptions(netWidth, bifoldType === "window");
  }, [bifoldType, netWidth]);

  const hasSingleHandle = withSingleHandle(
    withHandle,
    handleStyle,
    configuration ? configuration.split(" OR ")[0] : "",
  );

  const isPico = stileCategory === "Pico";

  const extShowSboltHandle =
    bifoldType === "window"
      ? false
      : !isAlum && !isPico
        ? false
        : !configuration
          ? false
          : (noAccess.includes(configuration) && withExtSbolt !== "No") ||
            !noAccess.includes(configuration);

  const alternateImages = extShowSboltHandle || hasSingleHandle;

  const handleErrMsgChange = useStore((state) => state.handleError);

  const configImage =
    configuration &&
    findBifoldImage(configuration, bifoldType, alternateImages);

  const singleImage = !/\s/.test(configuration || "");

  useEffect(() => {
    let update: UpdateObj = {};

    if (
      configurationOptions.length &&
      configuration &&
      !configurationOptions.includes(configuration)
    ) {
      update["configuration"] = "";
      update["exactConfig"] = "";
    }

    if (isAlum || isPico) {
      handleSboltExtErr(configuration || "");
    }

    if (exactConfig && exactConfig.length === 2 && configImage) {
      update["exactConfigImg"] = getConfigImg(
        configImage,
        primaryDoor || "",
        extShowSboltHandle,
        exactConfig.includes("L") ? "left" : "right",
      );
      return addToQuote(update);
    } else if (exactConfig && exactConfig.length === 4 && configImage) {
      const l = exactConfig.slice(0, 2);
      const r = exactConfig.slice(2);

      const numL = Number(l[0]);
      const numR = Number(r[0]);

      update["exactConfigImg"] = getConfigImg(
        configImage,
        primaryDoor || "",
        alternateImages,
        numL === numR || numL > numR ? "left" : "right",
      );
      return addToQuote(update);
    }
  }, [
    netWidth,
    bifoldType,
    stileCategory,
    extShowSboltHandle,
    hasSingleHandle,
    primaryDoor,
    exactConfig,
  ]);

  useEffect(() => {
    let update: UpdateObj = {};
    if (
      (bifoldType === "window" || withExtSbolt !== "Yes" || !isPico) &&
      evenEven.includes(configuration || "")
    ) {
      update["primaryDoor"] = "";
    }

    if (
      bifoldType === "window" ||
      (!isAlum && !isPico) ||
      !noAccess.includes(configuration || "")
    ) {
      update["withExtSbolt"] = "";
    }

    addToQuote(update);
    
    //primary door option might not exist for other stile/rail categories, so we need to remove the primary door error if it exists
    if(configuration){
      handlePrimaryErr(configuration);
    }
  }, [stileCategory]);

  const handlePrimaryErr = (config: string, withExtSboltSelected?: string) => {
    const currentOrSelectedWithExtSboltOption = withExtSboltSelected ?? withExtSbolt;
    if (bifoldType === "window" && evenEven.includes(config)) {
      return handleErrMsgChange("primaryDoorErr", "");
    } else if (
      config &&
      specifyPrimary.includes(config.split(" OR")[0]) &&
      !(
        (bifoldType === "window" || !isPico || currentOrSelectedWithExtSboltOption !== "Yes") &&
        evenEven.includes(config)
      ) &&
      !primaryDoor
    ) {
      return handleErrMsgChange("primaryDoorErr", "Select a Primary Door");
    }

    handleErrMsgChange("primaryDoorErr", "");
  };

  const handleSboltExtErr = (config: string) => {
    if (
      (isAlum || isPico) &&
      bifoldType !== "window" &&
      noAccess.includes(config) &&
      !withExtSbolt
    ) {
      return handleErrMsgChange(
        "withExtSboltErr",
        "Select an Exterior Access Option",
      );
    }

    handleErrMsgChange("withExtSboltErr", "");
  };

  const selectedConfig = (config: string) => {
    let update: SelectedConfigUpdateObj = {
      jamb: "",
      addScreen: "",
      screenPocket: "",
      screenColor: "",
      stilesAndRails: "",
      stileCategory: "",
      lockRail: "",
    };

    if (isPico) {
      if (shootBoltColor) {
        update["shootBoltColor"] =
          shootBoltColor === "Stainless Steel"
            ? "Brushed Chrome"
            : shootBoltColor;
      }

      if (hingeColor) {
        update["hingeColor"] = hingeColor === "Black" ? "Bronze" : hingeColor;
      }
    }

    handleErrMsgChange("jambErr", "");

    if (isAlum || isPico) {
      handleSboltExtErr(config);
    }

    if (!config) {
      update["exactConfig"] = "";
      update["exactConfigImg"] = "";
      update["configuration"] = "";
      update["primaryDoor"] = "";
      update["withExtSbolt"] = "";
    } else if (!/\s/.test(config)) {
      handlePrimaryErr(config);

      update["primaryDoor"] = "";
      update["withExtSbolt"] = "";
      update["configuration"] = config;
      update["exactConfig"] = config;
    } else {
      update["primaryDoor"] = "";
      update["withExtSbolt"] = "";
      update["configuration"] = config;
      update["exactConfig"] = "";
    }

    const noHandleAllowed = noAccess.includes(config);

    if (noHandleAllowed) {
      update["withHandle"] = "";
      update["handleStyle"] = "";
      update["handleColor"] = "";
      update["magnetCatch"] = "";
    } else if (isAlum) {
      update["withExtSbolt"] = "";
    }
    hideHandlesWithEvenConfigs(noHandleAllowed);

    addToQuote(update);
  };

  const handleConfigClick = (side: string) => {
    const chosen = configuration ? configuration.split("OR") : null;

    if (!chosen) return addToQuote({ exactConfig: "" });

    const index = side === "left" ? 0 : 1;

    let exactConfig = chosen[index].trim();

    let update = { exactConfig };

    const hasForbiddenHandles =
      withHandle === "Add Handle Set" &&
      (handleStyle === "Toulon" || handleStyle === "Savannah");

    if (hasForbiddenHandles && !singleDirectionOdds.includes(exactConfig)) {
      addToQuote({ handleStyle: "", handleColor: "" });
    }

    handlePrimaryErr(exactConfig);

    addToQuote(update);

    handleErrMsgChange("configErr", "");
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    addToQuote({ primaryDoor: value });
    handleErrMsgChange("primaryDoorErr", "");
  };

  const handleChangeSboltExt = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    addToQuote({ withExtSbolt: value });
    if (isPico && value === "No") {
      addToQuote({ withExtSbolt: value, primaryDoor: "" });
      handleErrMsgChange("primaryDoorErr", "");
    } else if (isPico && value === "Yes") {
      handlePrimaryErr(configuration || "", value);
    } else {
      addToQuote({ withExtSbolt: value });
    }
    handleErrMsgChange("withExtSboltErr", "");
  };

  const imageLeft = !configImage
    ? ""
    : alternateImages && configImage.image1LH && primaryDoor === "Left"
      ? configImage.image1LH
      : alternateImages && configImage.image1RH && primaryDoor === "Right"
        ? configImage.image1RH
        : alternateImages && configImage.image1LH && isPico
          ? configImage.image1RH
          : alternateImages && configImage.image1LH && !primaryDoor
            ? ""
            : configImage.image1;

  const imageRight = !configImage
    ? ""
    : alternateImages && configImage.image2LH && primaryDoor === "Left"
      ? configImage.image2LH
      : alternateImages && configImage.image2RH && primaryDoor === "Right"
        ? configImage.image2RH
        : alternateImages && configImage.image1LH && isPico
          ? configImage.image2RH
          : alternateImages && configImage.image2LH && !primaryDoor
            ? ""
            : configImage.image2;

  return {
    handleChange,
    handleConfigClick,
    selectedConfig,
    singleImage,
    configurationOptions,
    configuration,
    primaryDoor,
    netWidth,
    exactConfig,
    stileCategory,
    imageLeft,
    imageRight,
    handleChangeSboltExt,
    withExtSbolt,
    isPico,
    isAlum,
    material,
  };
}

export default useConfigSelection;
